import React from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { brl, pct } from "../lib/format";
import { Info, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { ProductImage } from "./ProductImage";

const Linha = ({ label, valor, forte = false, cor = "" }) => (
  <div className="flex items-baseline justify-between gap-2 py-[3px]">
    <span className="text-[12px] text-slate-500">{label}</span>
    <span className={`num text-[13px] ${forte ? "font-semibold text-slate-900" : ""} ${cor}`}>
      {valor}
    </span>
  </div>
);

export const ProductResultCard = ({ produto, onAdicionar, index = 0 }) => {
  const c = produto.calculo;

  return (
    <div
      className="sky-enter flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)]"
      style={{ animationDelay: `${Math.min(index, 8) * 25}ms` }}
      data-testid={`produto-card-${produto.codigo}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 gap-2.5">
          <ProductImage
            fotoPath={produto.foto_path}
            alt={produto.descricao_produto}
            className="h-12 w-12 rounded-lg border border-slate-100"
            iconClassName="h-5 w-5"
          />
          <div className="min-w-0">
          <Link
            to={`/produto/${produto.id}`}
            className="block font-display text-[15px] font-semibold leading-snug text-slate-900 line-clamp-2 hover:text-primary"
            title={produto.descricao_produto}
            data-testid={`produto-link-${produto.codigo}`}
          >
            {produto.descricao_produto}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="font-mono-num text-[11px] font-medium text-slate-500">
              {produto.codigo}
            </span>
            <span className="text-slate-300">•</span>
            <span className="font-mono-num text-[11px] text-slate-500">NCM {produto.ncm}</span>
            <Badge
              variant="secondary"
              className={`text-[10px] uppercase ${
                produto.origem === "IMPORTAÇÃO"
                  ? "bg-amber-100 text-amber-900"
                  : "bg-[#EEF2F9] text-[#1B315E]"
              }`}
            >
              {produto.origem === "IMPORTAÇÃO" ? "Importação" : "Fabricação"}
            </Badge>
          </div>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
            Preço com impostos
          </div>
          <div
            className="num font-display text-[26px] font-bold leading-tight text-slate-900 sky-value-change"
            key={c?.total_unitario}
            data-testid={`produto-total-${produto.codigo}`}
          >
            {brl(c?.total_unitario)}
          </div>
          <div className="text-[11px] text-slate-500">por {produto.unidade}</div>
        </div>
      </div>

      <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2">
        <Linha label="Valor de tabela" valor={brl(c?.valor_tabela)} forte />
        <Linha label={`IPI (${pct(c?.ipi_pct)})`} valor={`+ ${brl(c?.ipi_valor)}`} />
        <Linha
          label={`ST / DIFAL (${pct(c?.aliquota_st)})`}
          valor={c?.aliquota_st ? `+ ${brl(c?.st_valor)}` : "sem ST"}
          cor={c?.aliquota_st ? "text-slate-900" : "text-slate-400"}
        />
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button
          className="h-10 flex-1"
          onClick={() => onAdicionar(produto)}
          data-testid={`adicionar-orcamento-${produto.codigo}`}
        >
          <Plus className="mr-1.5 h-4 w-4" /> Adicionar ao orçamento
        </Button>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to={`/produto/${produto.id}`}>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  data-testid={`detalhe-${produto.codigo}`}
                >
                  <Info className="h-4 w-4" />
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent className="bg-slate-900 text-white">
              Ver detalhes e comparar enquadramentos
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="mt-2 text-[11px] text-slate-500">
        Desconto negociável no orçamento — máximo{" "}
        <b className="text-slate-700">{pct(produto.desconto_maximo)}</b>
        {produto.observacao ? ` • ${produto.observacao}` : ""}
      </div>
    </div>
  );
};
